import React from 'react';
import { ArrowDown, Users, ExternalLink } from 'lucide-react';
import { OrgData } from '../data';
import { View } from '../App';
import { UpdatePerson } from '../hooks/useOrgState';
import InlineEdit from './InlineEdit';
import { useAuth } from '../context/AuthContext';

interface Props {
  data: OrgData;
  setView: (v: View) => void;
  updatePerson: UpdatePerson;
}

export default function OrgView({ data, setView, updatePerson }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('editor');

  return (
    <div className="org-view">
      <div className="page-header">
        <h1>Organograma</h1>
        <p>Estrutura do setor de Integrações — recursos existentes organizados por tribo</p>
      </div>

      <div className="org-tree">
        {/* CTO */}
        <div className="org-level">
          <div className="org-card role-cto">
            <div className="org-card-role">Tecnologia</div>
            <div className="org-card-name">
              <InlineEdit
                value={data.cto.name}
                onChange={v => updatePerson(data.cto.id, 'name', v)}
              />
            </div>
            <div className="org-card-level">
              <InlineEdit
                value={data.cto.level ?? ''}
                onChange={v => updatePerson(data.cto.id, 'level', v)}
              />
            </div>
          </div>
        </div>

        <div className="org-connector">
          <ArrowDown size={16} />
        </div>

        {/* Coordinator */}
        <div className="org-level">
          <div className="org-card role-coord">
            <div className="org-card-role">Coordenação</div>
            <div className="org-card-name">
              <InlineEdit
                value={data.coordinator.name}
                onChange={v => updatePerson(data.coordinator.id, 'name', v)}
              />
            </div>
            <div className="org-card-level">
              <InlineEdit
                value={data.coordinator.level ?? ''}
                onChange={v => updatePerson(data.coordinator.id, 'level', v)}
              />
            </div>
          </div>
        </div>

        <div className="org-connector">
          <div className="org-branch-line" />
        </div>

        {/* Tribes */}
        <div className="org-tribes">
          {data.tribes.map(tribe => (
            <div key={tribe.id} className={`tribe-card tribe-${tribe.color}`}>
              <div className="tribe-card-header">
                <div className="tribe-card-name">{tribe.name}</div>
                <div className="tribe-card-tagline">{tribe.tagline}</div>
              </div>

              <div className="tribe-card-body">
                <div className="tribe-resource-group">
                  <div className="resource-label">
                    <Users size={12} />
                    Devs
                    <span className="resource-badge pending">reorganização</span>
                  </div>
                  {tribe.members.devs.map(p => (
                    <div key={p.id} className="resource-person pending-person">
                      <div className="person-avatar">{p.name[0]}</div>
                      <div>
                        <div className="person-name">
                          <InlineEdit disabled={!canEdit} value={p.name} onChange={v => updatePerson(p.id, 'name', v)} />
                        </div>
                        <div className="person-level">
                          <InlineEdit disabled={!canEdit} value={p.level ?? ''} onChange={v => updatePerson(p.id, 'level', v)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="tribe-resource-group">
                  <div className="resource-label">
                    <Users size={12} />
                    PO
                    <span className="resource-badge pending">reorganização</span>
                  </div>
                  {tribe.members.po.map(p => (
                    <div key={p.id} className="resource-person pending-person">
                      <div className="person-avatar">{p.name[0]}</div>
                      <div>
                        <div className="person-name">
                          <InlineEdit disabled={!canEdit} value={p.name} onChange={v => updatePerson(p.id, 'name', v)} />
                        </div>
                        <div className="person-level">
                          <InlineEdit disabled={!canEdit} value={p.level ?? ''} onChange={v => updatePerson(p.id, 'level', v)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="tribe-resource-group">
                  <div className="resource-label">
                    <Users size={12} />
                    Analistas
                    {tribe.id === 'cloud' && <span className="resource-badge active">no setor</span>}
                  </div>
                  {tribe.members.analistas.map(p => (
                    <div key={p.id} className={`resource-person ${tribe.id === 'cloud' ? 'active-person' : 'pending-person'}`}>
                      <div className="person-avatar">{p.name[0]}</div>
                      <div>
                        <div className="person-name">
                          <InlineEdit disabled={!canEdit} value={p.name} onChange={v => updatePerson(p.id, 'name', v)} />
                        </div>
                        <div className="person-level">
                          <InlineEdit disabled={!canEdit} value={p.level ?? ''} onChange={v => updatePerson(p.id, 'level', v)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="tribe-partners">
                  <div className="resource-label">Parceiros</div>
                  <div className="partners-list">
                    {tribe.partners.map(p => (
                      <span key={p.name} className={`partner-tag partner-${p.type}`}>{p.name}</span>
                    ))}
                  </div>
                </div>
              </div>

              <button className="tribe-cta" onClick={() => setView(tribe.id as View)}>
                Ver tribo completa
                <ExternalLink size={13} />
              </button>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="org-note">
          <div className="note-tag">Proposta de reorganização</div>
          <p>Os recursos de Dev e PO já existem no time de Tecnologia. A proposta é que passem a ter dedicação exclusiva às tribos, sem demandas paralelas — sem headcount novo.</p>
        </div>
      </div>
    </div>
  );
}
